import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { fmtDate } from "@/lib/utils";
import { PageHeader, ErrorBox, EmptyState } from "@/components/common";
import { useApp } from "@/lib/store";
import { Save, Settings as SettingsIcon, Palette, ShieldCheck, Brain, Search, KeyRound, CheckCircle2, XCircle, Trash2, Plus, Copy, Plug } from "lucide-react";

interface Workspace {
  id: string; name: string; slug: string; description: string;
  organization: string; organization_slug: string;
  accent_color: string;
  logo_url: string;
  default_retrieval_profile: string;
  default_llm_provider: string;
  default_llm_model: string;
  privacy_mode: "managed" | "byo_llm" | "byo_storage" | "on_prem";
  settings: Record<string, unknown>;
}

const PRIVACY_MODES = [
  { id: "managed",     label: "Managed (we host LLM + storage)" },
  { id: "byo_llm",     label: "BYO LLM (we host storage, you bring credentials)" },
  { id: "byo_storage", label: "BYO Storage (your S3 / object store)" },
  { id: "on_prem",     label: "On-prem (everything in your VPC)" },
];

export function WorkspaceSettings() {
  const wsId = useApp((s) => s.workspaceId);
  const workspaces = useApp((s) => s.workspaces);
  const setWorkspaces = useApp((s) => s.setWorkspaces);
  const qc = useQueryClient();

  const q = useQuery<Workspace>({
    queryKey: ["workspace", wsId],
    enabled: !!wsId,
    queryFn: () => rawFetch(`/iam/workspaces/${wsId}/`),
  });

  const [form, setForm] = useState<Partial<Workspace>>({});
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  const m = useMutation({
    mutationFn: (body: Partial<Workspace>) =>
      rawFetch<Workspace>(`/iam/workspaces/${wsId}/`, {
        method: "PATCH", body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setWorkspaces(workspaces.map((w) => (w.id === data.id ? { ...w, ...data } : w)));
      qc.invalidateQueries({ queryKey: ["workspace", wsId] });
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      // Apply accent color live.
      if (data.accent_color) {
        document.documentElement.style.setProperty(
          "--accent", hexToHsl(data.accent_color));
      }
    },
  });

  // Push the saved accent into the live CSS var for instant feedback.
  useEffect(() => {
    if (form.accent_color)
      document.documentElement.style.setProperty(
        "--accent", hexToHsl(form.accent_color));
  }, [form.accent_color]);

  if (!wsId) {
    return (
      <>
        <PageHeader title="Workspace settings" subtitle="Tailor this workspace." />
        <EmptyState title="No workspace selected"
          hint="Pick a workspace from the header dropdown." />
      </>
    );
  }

  function update<K extends keyof Workspace>(k: K, v: Workspace[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function save() {
    if (!form) return;
    const body: Partial<Workspace> = {
      name: form.name, description: form.description,
      accent_color: form.accent_color,
      logo_url: form.logo_url ?? "",
      default_retrieval_profile: form.default_retrieval_profile,
      default_llm_provider: form.default_llm_provider,
      default_llm_model: form.default_llm_model,
      privacy_mode: form.privacy_mode,
    };
    m.mutate(body);
  }

  return (
    <>
      <PageHeader title="Workspace settings"
                  subtitle={form.name || "—"}
                  actions={
                    <button className="btn-primary" disabled={m.isPending} onClick={save}>
                      <Save size={14} /> {m.isPending ? "Saving…" : "Save changes"}
                    </button>
                  } />
      {q.isError && <ErrorBox error={q.error} />}
      {m.isError && <ErrorBox error={m.error} />}
      {m.isSuccess && (
        <div className="card mb-4 border-good/40 text-sm text-good">Saved.</div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* --- General -------------------------------------------------- */}
        <div className="card">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <SettingsIcon size={16} className="text-accent" /> General
          </div>
          <label className="label">Name</label>
          <input className="input mb-3" value={form.name ?? ""}
                 onChange={(e) => update("name", e.target.value)} />
          <label className="label">Slug</label>
          <input className="input mb-3" value={form.slug ?? ""} disabled
                 title="Slug is immutable." />
          <label className="label">Description</label>
          <textarea className="input mb-1 h-24" value={form.description ?? ""}
                    onChange={(e) => update("description", e.target.value)} />
        </div>

        {/* --- Branding ------------------------------------------------- */}
        <div className="card">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Palette size={16} className="text-accent" /> Branding
          </div>
          <label className="label">Accent color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              className="h-10 w-14 cursor-pointer rounded border border-border bg-bg"
              value={form.accent_color ?? "#38bdf8"}
              onChange={(e) => update("accent_color", e.target.value)} />
            <input className="input font-mono text-xs"
                   value={form.accent_color ?? ""}
                   onChange={(e) => update("accent_color", e.target.value)} />
          </div>
          <p className="mt-2 text-[11px] text-muted">
            Drives the Studio's primary highlight (buttons, focus rings, badges).
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button className="btn-primary" type="button">Sample primary</button>
            <button className="btn" type="button">Sample default</button>
            <span className="pill-good">good</span>
            <span className="pill-warn">warn</span>
            <span className="pill-bad">bad</span>
          </div>

          <div className="mt-5 border-t border-border pt-4">
            <label className="label">Company logo</label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-bg">
                {form.logo_url
                  ? <img src={form.logo_url} alt="logo"
                         className="max-h-12 max-w-12 object-contain" />
                  : <span className="text-[10px] text-muted">no logo</span>}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="block w-full text-xs file:mr-3 file:rounded file:border-0 file:bg-accent/20 file:px-3 file:py-1.5 file:text-text hover:file:bg-accent/30"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 256 * 1024) {
                      alert("Logo must be < 256 KB. Optimize the image and try again.");
                      e.target.value = "";
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      const url = String(reader.result || "");
                      update("logo_url", url);
                    };
                    reader.readAsDataURL(f);
                  }}
                />
                <p className="mt-1 text-[11px] text-muted">
                  PNG / JPG / SVG / WebP, max 256&nbsp;KB. Saved as an inline
                  data URL — appears in the top bar instead of the Ontoligent
                  mark for this workspace's users.
                </p>
                {form.logo_url && (
                  <button
                    type="button"
                    className="mt-1 text-[11px] text-muted underline hover:text-bad"
                    onClick={() => update("logo_url", "")}
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- Defaults ------------------------------------------------- */}
        <div className="card">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Search size={16} className="text-accent" /> Retrieval defaults
          </div>
          <label className="label">Default retrieval profile name</label>
          <input className="input mb-3" placeholder="e.g. acme-hybrid-default"
                 value={form.default_retrieval_profile ?? ""}
                 onChange={(e) => update("default_retrieval_profile", e.target.value)} />
          <p className="text-[11px] text-muted">
            Used by agents that don't pin their own retrieval profile.
          </p>
        </div>

        <div className="card">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Brain size={16} className="text-accent" /> Default LLM
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Provider</label>
              <select className="input"
                      value={form.default_llm_provider ?? ""}
                      onChange={(e) => update("default_llm_provider", e.target.value)}>
                <option value="">—</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                <option value="ollama">Ollama (local)</option>
                <option value="vllm">vLLM</option>
              </select>
            </div>
            <div>
              <label className="label">Model</label>
              <input className="input" placeholder="e.g. gpt-4o, claude-3-5-sonnet"
                     value={form.default_llm_model ?? ""}
                     onChange={(e) => update("default_llm_model", e.target.value)} />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted">
            Pair with a credential in <a href="/governance" className="text-accent underline">Governance → Credentials</a>.
          </p>
        </div>

        {/* --- Privacy ------------------------------------------------- */}
        <div className="card lg:col-span-2">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <ShieldCheck size={16} className="text-accent" /> Data sovereignty
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PRIVACY_MODES.map((p) => (
              <label
                key={p.id}
                className={`card cursor-pointer ${form.privacy_mode === p.id ? "border-accent shadow-glow" : ""}`}
              >
                <div className="flex items-start gap-2">
                  <input type="radio" name="privacy" className="mt-1"
                         checked={form.privacy_mode === p.id}
                         onChange={() => update("privacy_mode", p.id as Workspace["privacy_mode"])} />
                  <div>
                    <div className="text-sm">{p.label}</div>
                    <div className="text-[11px] text-muted">{descPrivacy(p.id)}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* --- BYOK credentials --------------------------------------- */}
        <div className="card lg:col-span-2">
          <CredentialsPanel
            workspaceId={wsId}
            defaultProvider={form.default_llm_provider}
            defaultModel={form.default_llm_model}
          />
        </div>

        {/* --- API keys ----------------------------------------------- */}
        <div className="card lg:col-span-2">
          <ApiKeysPanel workspaceId={wsId} workspaceSlug={form.slug} />
        </div>
      </div>
    </>
  );
}

function descPrivacy(id: string): string {
  switch (id) {
    case "managed":     return "Fastest to start. Vector store + LLM run in the Ontoligent managed plane.";
    case "byo_llm":     return "We host the vectors; every LLM call uses your credentials and your model endpoints.";
    case "byo_storage": return "Original uploads use your S3 bucket. Extracted text, chunks, and metadata still persist in Ontoligent's database; this is not zero-retention.";
    case "on_prem":     return "Requires a separate deployment inside your VPC. Selecting this mode alone does not move existing data.";
    default:            return "";
  }
}

// ===========================================================================
// API keys panel
// ===========================================================================
interface ApiKeyRow {
  id: string; label: string; prefix: string; scopes: string[];
  last_used_at: string | null; expires_at: string | null;
  revoked_at: string | null; created_at: string;
}

function ApiKeysPanel({ workspaceId, workspaceSlug }:
  { workspaceId: string; workspaceSlug?: string }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<{ raw: string; label: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const q = useQuery<{ results?: ApiKeyRow[] } | ApiKeyRow[]>({
    queryKey: ["apikeys", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => rawFetch(`/iam/apikeys/?workspace=${workspaceId}`),
  });
  const rows: ApiKeyRow[] = Array.isArray(q.data) ? q.data : (q.data?.results ?? []);

  const createM = useMutation({
    mutationFn: (body: { workspace: string; label: string }) =>
      rawFetch<ApiKeyRow & { raw: string }>(`/iam/apikeys/`, {
        method: "POST", body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setCreated({ raw: data.raw, label: data.label });
      setLabel("");
      qc.invalidateQueries({ queryKey: ["apikeys", workspaceId] });
    },
  });

  const revokeM = useMutation({
    mutationFn: (id: string) =>
      rawFetch(`/iam/apikeys/${id}/`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apikeys", workspaceId] }),
  });

  async function copyKey(raw: string) {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — user can select manually */ }
  }

  const active = rows.filter((r) => !r.revoked_at);

  return (
    <>
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <KeyRound size={16} className="text-accent" /> API keys
      </div>
      <p className="mb-4 text-[11px] text-muted">
        Use an API key to reach this workspace from <strong>your own apps</strong> —
        ingest documents, run retrieval, and call your agents from a website,
        backend, batch job, or a customer-facing chatbot. Send it as
        <code className="mx-1">Authorization: ApiKey &lt;key&gt;</code> together
        with <code className="mx-1">X-Tenant-Schema: {workspaceSlug || "<slug>"}</code>.
        Keys are scoped to this workspace, server-side only, and shown in full
        once. The MCP endpoint (<code>POST /api/v1/mcp/rpc</code>) accepts the same key.
      </p>

      {/* One-time reveal of a freshly created key */}
      {created && (
        <div className="card mb-4 border-good/40">
          <div className="mb-2 flex items-center gap-2 text-sm text-good">
            <CheckCircle2 size={15} /> Key “{created.label}” created — copy it now.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-bg px-2 py-1.5 font-mono text-xs">
              {created.raw}
            </code>
            <button className="btn" type="button" onClick={() => copyKey(created.raw)}>
              <Copy size={14} /> {copied ? "Copied" : "Copy"}
            </button>
            <button className="btn-ghost" type="button" onClick={() => setCreated(null)}>
              <XCircle size={14} />
            </button>
          </div>
          <p className="mt-2 text-[11px] text-bad">
            This is the only time the full key is displayed. Store it in a secret
            manager — you cannot retrieve it later.
          </p>
        </div>
      )}

      {/* Create form */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <label className="label">New key label</label>
          <input className="input" placeholder="e.g. ci-pipeline, my-laptop"
                 value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <button className="btn-primary"
                disabled={!label.trim() || createM.isPending}
                onClick={() => createM.mutate({ workspace: workspaceId, label: label.trim() })}>
          <Plus size={14} /> {createM.isPending ? "Creating…" : "Create key"}
        </button>
      </div>
      {createM.isError && <ErrorBox error={createM.error} />}

      {/* Existing keys */}
      {q.isLoading ? (
        <p className="text-xs text-muted">Loading…</p>
      ) : active.length === 0 ? (
        <p className="text-xs text-muted">No active keys yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted">
              <tr className="border-b border-border">
                <th className="py-1.5 pr-3 font-medium">Label</th>
                <th className="py-1.5 pr-3 font-medium">Prefix</th>
                <th className="py-1.5 pr-3 font-medium">Created</th>
                <th className="py-1.5 pr-3 font-medium">Last used</th>
                <th className="py-1.5 pr-3 font-medium">Expires</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {active.map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="py-1.5 pr-3">{r.label}</td>
                  <td className="py-1.5 pr-3 font-mono text-muted">{r.prefix}…</td>
                  <td className="py-1.5 pr-3 text-muted">{fmtDate(r.created_at)}</td>
                  <td className="py-1.5 pr-3 text-muted">{r.last_used_at ? fmtDate(r.last_used_at) : "never"}</td>
                  <td className="py-1.5 pr-3 text-muted">{r.expires_at ? fmtDate(r.expires_at) : "—"}</td>
                  <td className="py-1.5 text-right">
                    <button className="btn-ghost text-bad"
                            title="Revoke key"
                            disabled={revokeM.isPending}
                            onClick={() => { if (confirm(`Revoke key “${r.label}”? This cannot be undone.`)) revokeM.mutate(r.id); }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quickstart — call an agent from your own app */}
      <div className="mt-5 border-t border-border pt-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-medium">
          <Plug size={14} className="text-accent" /> Use it in your app
        </div>
        <p className="mb-2 text-[11px] text-muted">
          Ask one of your agents and get the grounded, cited answer in a single
          call. Replace <code className="mx-1">&lt;agent-id&gt;</code> with an
          agent from <Link to="/agents" className="text-accent underline">Agents</Link>.
        </p>
        <pre className="overflow-x-auto rounded bg-bg p-3 text-[10px] leading-snug">
{`# synchronous: one call, answer in the response
curl -X POST https://api.ontoligent.io/api/v1/agents/agents/<agent-id>/ask/ \\
  -H "Authorization: ApiKey <your-key>" \\
  -H "X-Tenant-Schema: ${workspaceSlug || "<slug>"}" \\
  -H "Content-Type: application/json" \\
  -d '{"message":"Hello","thread_id":"case-001"}'
# -> 200 { "status":"succeeded", "output": { "content":"…" } }

# long / supervisor jobs: submit (async) then poll the run
# POST …/agents/agents/<agent-id>/chat/  ->  GET …/agents/runs/<run-id>/`}
        </pre>
        <p className="mt-2 text-[11px] text-muted">
          Full guide with ingest + retrieval examples:{" "}
          <Link to="/docs" className="text-accent underline">Docs &rarr; Embed in your app</Link>.
        </p>
      </div>
    </>
  );
}

function hexToHsl(hex: string): string {
  const m = /^#?([a-f0-9]{6})$/i.exec(hex);
  if (!m) return "199 89% 55%";
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8)  & 255) / 255;
  const b = ( n        & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// ===========================================================================
// BYOK Credentials panel
// ===========================================================================
interface ProviderRec {
  id: string;
  label: string;
  needs_key: boolean;
  key_help: string;
  /** When the provider is single-kind (e.g. neo4j → "graph"), the BE tags
   *  it so the UI can auto-narrow the kind selector instead of letting the
   *  user nonsensically pick "llm" for Neo4j. */
  kind?: string;
  /** Per-provider form schema for non-LLM credentials (graph / vector /
   *  object_store). When present we render these inputs instead of the
   *  LLM "API key + model" form. */
  fields?: string[];
  models: { id: string; label: string; multimodal: boolean; kind?: string }[];
}
interface Credential {
  id: string; workspace: string; kind: string; provider: string;
  model: string; alias: string; label: string; base_url: string;
  extra: Record<string, string>;
  enabled: boolean; status: string; created_at: string;
}
interface CatalogAiChoice { credential_ids: string[]; enabled: boolean; message: string }
interface UploadStoreChoice { credential_id: string }
interface ResolvedModel {
  provider: string; kind: string; model: string;
  customer_owned: boolean; source: string; has_key: boolean; base_url: string;
}

function CredentialsPanel({
  workspaceId, defaultProvider, defaultModel,
}: {
  workspaceId: string;
  defaultProvider: string | undefined;
  defaultModel: string | undefined;
}) {
  const qc = useQueryClient();
  const recs = useQuery<{ providers: ProviderRec[]; kinds: string[] }>({
    queryKey: ["provider-recs"],
    queryFn: () => rawFetch(`/iam/providers/recommendations/`),
  });
  const creds = useQuery<{ results?: Credential[] } | Credential[]>({
    queryKey: ["credentials", workspaceId],
    queryFn: () => rawFetch(`/iam/credentials/?workspace=${workspaceId}`),
  });
  const list: Credential[] = Array.isArray(creds.data)
    ? creds.data
    : (creds.data?.results ?? []);

  const resolved = useQuery<ResolvedModel>({
    queryKey: ["resolve-cred", workspaceId, defaultProvider],
    enabled: !!defaultProvider,
    queryFn: () => rawFetch(
      `/iam/credentials/resolve/?workspace=${workspaceId}&provider=${defaultProvider}&kind=llm`,
    ),
  });

  const [provider, setProvider] = useState("openai");
  const [kind, setKind] = useState("llm");
  const [model, setModel] = useState("");
  const [label, setLabel] = useState("default");
  const [secret, setSecret] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  /** Per-provider dynamic fields (e.g. neo4j uri/username/database,
   *  pinecone index/environment, s3 bucket/region/...). The "secret-like"
   *  field is stored separately in ``secret`` and the rest are POSTed in
   *  ``extra`` so the BE persists them on ProviderCredential.extra. */
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = list.find((item) => item.id === editingId);
  const catalogAi = useQuery<CatalogAiChoice>({
    queryKey: ["catalog-ai-credentials", workspaceId],
    queryFn: () => rawFetch(`/iam/credentials/catalog-ai/?workspace=${workspaceId}`),
  });
  const uploadStore = useQuery<UploadStoreChoice>({
    queryKey: ["upload-store", workspaceId],
    queryFn: () => rawFetch(`/iam/credentials/upload-store/?workspace=${workspaceId}`),
  });
  const [catalogOrder, setCatalogOrder] = useState<string[]>([]);
  const [catalogEnabled, setCatalogEnabled] = useState(true);
  useEffect(() => {
    if (!catalogAi.data) return;
    setCatalogOrder(catalogAi.data.credential_ids);
    setCatalogEnabled(catalogAi.data.enabled);
  }, [catalogAi.data]);

  const selectedProvider = recs.data?.providers.find((p) => p.id === provider);

  /** A field name is treated as the "secret" (encrypted at rest) if it
   *  matches one of these. Everything else goes into ``extra``. */
  const SECRET_FIELDS = new Set(["api_key", "password", "secret_key", "token"]);
  /** Friendly labels + input types for known dynamic field names. */
  const FIELD_META: Record<string, { label: string; type: string; placeholder?: string }> = {
    uri:           { label: "Bolt URI",        type: "text",     placeholder: "neo4j+s://xxx.databases.neo4j.io" },
    username:      { label: "Username",        type: "text",     placeholder: "neo4j" },
    password:      { label: "Password",        type: "password", placeholder: "••••••••" },
    database:      { label: "Database",        type: "text",     placeholder: "neo4j" },
    api_key:       { label: "API key",         type: "password", placeholder: "••••••••" },
    index:         { label: "Index name",      type: "text",     placeholder: "ontoligent-prod" },
    environment:   { label: "Environment",     type: "text",     placeholder: "us-east-1-aws" },
    url:           { label: "Cluster URL",     type: "text",     placeholder: "https://xxx.weaviate.cloud" },
    bucket:        { label: "Bucket",          type: "text",     placeholder: "ontoligent-data" },
    region:        { label: "Region",          type: "text",     placeholder: "AWS: us-east-1 · DigitalOcean: nyc3" },
    endpoint_url:  { label: "Endpoint URL",    type: "text",     placeholder: "DigitalOcean: https://nyc3.digitaloceanspaces.com" },
    access_key:    { label: "Access key ID",   type: "text",     placeholder: "AKIA..." },
    secret_key:    { label: "Secret key",      type: "password", placeholder: "••••••••" },
    token:         { label: "Token",           type: "password", placeholder: "••••••••" },
  };

  /** When the provider changes, auto-narrow ``kind`` if the provider is
   *  single-kind, default the model, and reset the dynamic field map so
   *  stale neo4j fields don't leak into an S3 form. */
  function onProviderChange(next: string) {
    setEditingId(null);
    setProvider(next);
    setModel("");
    setSecret("");
    setBaseUrl("");
    setFieldValues({});
    const p = recs.data?.providers.find((x) => x.id === next);
    if (p?.kind) setKind(p.kind);
    else setKind("llm");
    // Suggest a sensible default alias so users can add multiples
    // without colliding on the (workspace, kind, provider, label) unique
    // constraint. ``openai-default`` / ``neo4j-default`` / ...
    setLabel(`${next}-default`);
  }

  /** Validate "can submit" per current form shape. */
  function canSubmit(): boolean {
    if (create.isPending) return false;
    if (!label.trim()) return false;
    if (selectedProvider?.fields?.length) {
      // Datastore form: every declared field must be filled OR be
      // explicitly optional (only endpoint_url is optional today).
      for (const f of selectedProvider.fields) {
        if (f === "endpoint_url" || (editing && SECRET_FIELDS.has(f))) continue;
        if (!(fieldValues[f] || "").trim()) return false;
      }
      if (provider === "bedrock" && !model.trim()) return false;
      return true;
    }
    // LLM form
    return !!model.trim() && !(selectedProvider?.needs_key && !secret && !editing);
  }

  /** Build the POST body. For LLM providers it's the current behaviour;
   *  for datastore providers we collapse the dynamic fields into
   *  ``extra`` (and the password/secret_key/api_key into ``secret``). */
  function buildBody() {
    if (selectedProvider?.fields?.length) {
      const extra: Record<string, string> = {};
      let datastoreSecret = "";
      for (const f of selectedProvider.fields) {
        const v = (fieldValues[f] || "").trim();
        if (SECRET_FIELDS.has(f)) {
          // Use the first secret-like field as the encrypted secret. If
          // multiple are declared (e.g. s3 has access_key + secret_key),
          // the non-secret one stays in ``extra`` so the gateway can read
          // both at call time.
          if (!datastoreSecret && v) datastoreSecret = v;
          else if (v) extra[f] = v;
        } else if (v) {
          extra[f] = v;
        }
      }
      return {
        workspace: workspaceId,
        provider, kind,
        model: provider === "bedrock" ? model : "",
        alias: label,
        // ``secret`` is required by the BE serializer. Stuff a placeholder
        // when the form has none yet — canSubmit() prevents that path.
        secret: datastoreSecret,
        base_url: extra.url || extra.endpoint_url || "",
        extra,
        enabled: editing?.enabled ?? true,
      };
    }
    return {
      workspace: workspaceId, provider, kind, model,
      alias: label, secret, base_url: baseUrl, extra: editing?.extra ?? {},
      enabled: editing?.enabled ?? true,
    };
  }

  function editCredential(item: Credential) {
    setEditingId(item.id);
    setProvider(item.provider); setKind(item.kind); setModel(item.model);
    setLabel(item.alias); setBaseUrl(item.base_url);
    setSecret(""); setFieldValues(item.extra ?? {});
  }

  const create = useMutation({
    mutationFn: () => rawFetch<Credential>(editingId
      ? `/iam/credentials/${editingId}/` : `/iam/credentials/`, {
      method: editingId ? "PATCH" : "POST",
      body: JSON.stringify(buildBody()),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials", workspaceId] });
      qc.invalidateQueries({ queryKey: ["resolve-cred"] });
      setSecret(""); setFieldValues({});
      setEditingId(null);
      // Keep alias visible so the user sees what they just added; the
      // unique constraint will force them to rename for the next one.
    },
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      rawFetch(`/iam/credentials/${id}/`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credentials", workspaceId] }),
  });

  const test = useMutation({
    mutationFn: (id: string) =>
      rawFetch<{ ok: boolean; message: string; latency_ms: number; model: string }>(
        `/iam/credentials/${id}/test/`, { method: "POST" }),
  });
  const useForUploads = useMutation({
    mutationFn: (id: string) => rawFetch(`/iam/credentials/${id}/use-for-uploads/`, {
      method: "POST",
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["upload-store", workspaceId] }),
  });
  const toggle = useMutation({
    mutationFn: (item: Credential) => rawFetch(`/iam/credentials/${item.id}/`, {
      method: "PATCH", body: JSON.stringify({ enabled: !item.enabled }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credentials", workspaceId] }),
  });
  const saveCatalogAi = useMutation({
    mutationFn: () => rawFetch(`/iam/credentials/catalog-ai/?workspace=${workspaceId}`, {
      method: "PUT", body: JSON.stringify({ credential_ids: catalogOrder, enabled: catalogEnabled }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-ai-credentials", workspaceId] }),
  });

  return (
    <>
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <KeyRound size={16} className="text-accent" /> API keys & models (BYOK)
      </div>

      {/* Resolved-model banner so users see exactly what runs */}
      {defaultProvider && (
        <div className="card mb-3 border-accent/30 bg-bg/40 text-xs">
          <div className="mb-1 font-medium text-fg">
            Agents in this workspace currently call:
          </div>
          {resolved.data ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="pill-good">{resolved.data.provider}</span>
              <span className="font-mono">{resolved.data.model || defaultModel || "—"}</span>
              <span className={resolved.data.customer_owned ? "pill-good" : "pill-warn"}>
                {resolved.data.customer_owned ? "your key" : "platform default"}
              </span>
              {!resolved.data.has_key && (
                <span className="pill-bad">no key configured</span>
              )}
            </div>
          ) : <span className="text-muted">Resolving…</span>}
        </div>
      )}

      {/* Existing credentials list */}
      <p className="mb-2 text-xs text-muted">An S3 connection lets workspace admins test a bucket, choose it for uploads, and browse selected CSV/XLSX objects for in-memory term discovery. Connecting does not scan or sync the bucket automatically. Catalog file uploads may write originals to the selected workspace bucket; cloud dictionary import only reads selected objects and leaves them there.</p>
      {list.length === 0 ? (
        <div className="text-xs text-muted">
          No credentials yet. Catalog file uploads require an enabled workspace
          S3 credential in every storage mode. Add one here and test its access.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-muted">
            <tr>
              <th className="py-1 text-left">Provider</th>
              <th className="text-left">Kind</th>
              <th className="text-left">Model</th>
              <th className="text-left">Alias</th>
              <th className="text-left">Enabled</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-border">
                <td className="py-1 font-mono">{c.provider}</td>
                <td>{c.kind}</td>
                <td className="font-mono">{c.model || "—"}</td>
                <td>{c.alias}</td>
                <td>
                  <span className={c.enabled ? "pill-good" : "pill-warn"}>{c.status}</span>
                </td>
                <td className="text-right">
                  <button className="btn px-2 py-0.5" onClick={() => editCredential(c)}>
                    Edit
                  </button>{" "}
                  <button className="btn px-2 py-0.5" onClick={() => toggle.mutate(c)}>
                    {c.enabled ? "Disable" : "Enable"}
                  </button>{" "}
                  <button
                    className="btn px-2 py-0.5"
                    onClick={() => test.mutate(c.id)}
                    title="For S3, write/read/delete a disposable object; for model providers, send a small probe"
                  >
                    Test
                  </button>{" "}
                  {c.provider === "s3" && c.kind === "object_store" && c.enabled && <>
                    <button className="btn px-2 py-0.5" disabled={useForUploads.isPending}
                      onClick={() => useForUploads.mutate(c.id)}
                      title="Re-test this bucket, then select it for new Catalog uploads">
                      {uploadStore.data?.credential_id === c.id ? "Used for uploads" : "Use for uploads"}
                    </button>{" "}
                  </>}
                  <button
                    className="btn px-2 py-0.5"
                    onClick={() => {
                      if (confirm(`Delete credential "${c.alias}"? Existing uploads that use it may no longer be reprocessed.`)) del.mutate(c.id);
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {test.data && (
        <div className={`mt-2 flex items-center gap-2 text-xs ${test.data.ok ? "text-good" : "text-bad"}`}>
          {test.data.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
          {test.data.message} <span className="text-muted">({test.data.latency_ms}ms)</span>
        </div>
      )}
      {useForUploads.isError && <ErrorBox error={useForUploads.error} />}
      {useForUploads.isSuccess && <p role="status" className="mt-2 text-xs text-good">Workspace upload bucket selected and tested.</p>}

      <div className="mt-4 border-t border-border pt-3 text-xs">
        <div className="mb-1 font-medium">Use for Catalog AI</div>
        <p className="mb-2 text-muted">Choose your workspace key, then order any fallbacks. No platform key is used.</p>
        {list.filter((c) => c.enabled && (c.kind === "llm" || c.kind === "vlm")).map((c) => {
          const index = catalogOrder.indexOf(c.id);
          return <div key={c.id} className="flex items-center gap-2 border-t border-border py-1">
            <label className="flex min-w-0 flex-1 items-center gap-2">
              <input type="checkbox" checked={index >= 0} onChange={() => setCatalogOrder(
                index >= 0 ? catalogOrder.filter((id) => id !== c.id) : [...catalogOrder, c.id]
              )} />
              <span className="truncate">{c.provider} / {c.alias} / {c.model}</span>
            </label>
            {index >= 0 && <>
              <button className="btn px-2" aria-label={`Move ${c.alias} earlier`} disabled={index === 0}
                onClick={() => setCatalogOrder((old) => {
                  const next = [...old]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next;
                })}>↑</button>
              <button className="btn px-2" aria-label={`Move ${c.alias} later`} disabled={index === catalogOrder.length - 1}
                onClick={() => setCatalogOrder((old) => {
                  const next = [...old]; [next[index + 1], next[index]] = [next[index], next[index + 1]]; return next;
                })}>↓</button>
            </>}
          </div>;
        })}
        <label className="mt-2 flex items-center gap-2">
          <input type="checkbox" checked={catalogEnabled} onChange={(e) => setCatalogEnabled(e.target.checked)} />
          Enable Catalog AI
        </label>
        <button className="btn mt-2" onClick={() => saveCatalogAi.mutate()} disabled={saveCatalogAi.isPending}>
          Save Catalog AI selection
        </button>
        {saveCatalogAi.isError && <ErrorBox error={saveCatalogAi.error} />}
        {saveCatalogAi.isSuccess && <span className="ml-2 text-good">Saved.</span>}
        {catalogOrder.length === 0 && <p className="mt-2 text-muted">Add your LLM key to enable this</p>}
      </div>

      {/* Add credential form — adapts to provider:
          - LLM-style providers (openai/anthropic/google/ollama/groq) show
            API key + model dropdown + optional base URL.
          - Datastore providers (neo4j/pinecone/weaviate/qdrant/s3) show
            their declared field list and lock the kind to the provider's
            native kind (graph/vector/object_store) so users can't pick
            nonsense combinations like "neo4j as LLM".
          Multiple credentials per provider are supported by changing the
          alias (BE enforces unique(workspace, kind, provider, label)). */}
      <div className="mt-4 border-t border-border pt-3">
        <div className="mb-2 text-xs font-medium text-muted">{editing ? `Edit ${editing.alias}` : "Add a credential"}</div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="label">Provider</label>
            <select className="input" value={provider} disabled={!!editing}
                    onChange={(e) => onProviderChange(e.target.value)}>
              <optgroup label="LLM / embedder">
                {recs.data?.providers.filter((p) => !p.kind).map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </optgroup>
              <optgroup label="Graph database">
                {recs.data?.providers.filter((p) => p.kind === "graph").map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </optgroup>
              <optgroup label="Vector store">
                {recs.data?.providers.filter((p) => p.kind === "vector").map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </optgroup>
              <optgroup label="Object store">
                {recs.data?.providers.filter((p) => p.kind === "object_store").map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="label">Kind</label>
            <select className="input" value={kind}
                    disabled={!!selectedProvider?.kind}
                    title={selectedProvider?.kind ? `${selectedProvider.label} is a ${selectedProvider.kind} credential — kind is fixed.` : undefined}
                    onChange={(e) => setKind(e.target.value)}>
              {selectedProvider?.kind ? (
                <option value={selectedProvider.kind}>{selectedProvider.kind}</option>
              ) : (
                ["llm", "embedder", "vlm"].map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="label">Alias</label>
            <input className="input" value={label}
                   placeholder="e.g. openai-default, openai-fallback, neo4j-prod"
                   onChange={(e) => setLabel(e.target.value)} />
          </div>

          {/* ===== Branch A — LLM/embedder/vlm providers ===== */}
          {!selectedProvider?.fields?.length && (
            <>
              <div className="sm:col-span-1">
                <label className="label">Model (recommended)</label>
                <input className="input" list="recommended-models" value={model}
                       onChange={(e) => setModel(e.target.value)} placeholder="Model name" />
                <datalist id="recommended-models">{selectedProvider?.models
                  .filter((m) => !m.kind || m.kind === kind)
                  .map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</datalist>
              </div>
              <div className="sm:col-span-2">
                <label className="label">API key</label>
                <input className="input font-mono" type="password" value={secret}
                       placeholder={selectedProvider?.needs_key ? "sk-..." : "(not required)"}
                       onChange={(e) => setSecret(e.target.value)} />
                <p className="mt-1 text-[11px] text-muted">{selectedProvider?.key_help}</p>
              </div>
              <div className="sm:col-span-3">
                <label className="label">Base URL (optional — Azure / vLLM / Ollama)</label>
                <input className="input font-mono" value={baseUrl}
                       placeholder="https://your-endpoint/v1"
                       onChange={(e) => setBaseUrl(e.target.value)} />
              </div>
            </>
          )}

          {/* ===== Branch B — Datastore providers (graph/vector/object_store) ===== */}
          {selectedProvider?.fields?.length ? (
            <>
              {selectedProvider.fields.map((f) => {
                const meta = FIELD_META[f] ?? { label: f, type: "text" };
                return (
                  <div key={f} className={f === "uri" || f === "url" || f === "endpoint_url" ? "sm:col-span-3" : "sm:col-span-1"}>
                    <label className="label">{meta.label}{f === "endpoint_url" ? " (optional)" : ""}</label>
                    <input
                      className="input font-mono"
                      type={meta.type}
                      value={fieldValues[f] ?? ""}
                      placeholder={meta.placeholder}
                      onChange={(e) => setFieldValues((prev) => ({ ...prev, [f]: e.target.value }))}
                    />
                  </div>
                );
              })}
              {provider === "bedrock" && <div className="sm:col-span-3">
                <label className="label">Bedrock model ID</label>
                <input className="input" value={model} onChange={(e) => setModel(e.target.value)} />
              </div>}
              <div className="sm:col-span-3">
                <p className="text-[11px] text-muted">{selectedProvider.key_help}</p>
                {provider === "s3" && (
                  <p className="mt-1 text-[11px] text-muted">
                    DigitalOcean Spaces needs a matching region and endpoint (for example,
                    <code> nyc3</code> and <code>https://nyc3.digitaloceanspaces.com</code>).
                    Use a bucket-scoped Read/Write/Delete key, then press Test above.
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button className="btn-primary"
                  disabled={!canSubmit()}
                  onClick={() => create.mutate()}>
            <Plus size={14} /> {create.isPending ? "Saving…" : editing ? "Save changes" : "Add credential"}
          </button>
          {editing && <button className="btn" onClick={() => { setEditingId(null); setSecret(""); setFieldValues({}); }}>
            Cancel edit
          </button>}
          {create.isError && <ErrorBox error={create.error} />}
          {create.isSuccess && <span className="text-xs text-good">Stored.</span>}
        </div>
        <p className="mt-3 text-[11px] text-muted">
          Secrets are encrypted with Fernet (key derived from <code>DJANGO_SECRET_KEY</code>)
          and never returned by any read endpoint. You can add multiple
          credentials per provider (e.g. <code>openai-default</code> +
          <code> openai-fallback</code>) — the gateway picks them by alias or
          falls back in declared order. Delete a credential to revoke.
        </p>
      </div>
    </>
  );
}
